import { ArrowUpIcon, CircleAlertIcon, LoaderCircleIcon, PaperclipIcon, SquareIcon, XIcon } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState, type DragEvent, type KeyboardEvent } from "react";
import { toast } from "sonner";
import type { Attachment } from "../../../../shared/api.ts";
import { Button } from "@/components/ui/button.tsx";
import { Tooltip } from "@/components/ui/tooltip.tsx";
import { useI18n } from "@/i18n/index.tsx";
import { errorMessage } from "@/lib/hooks.ts";
import { uploadFile } from "@/lib/http.ts";
import { cn } from "@/lib/utils.ts";
import { ATTACHMENT_ICONS } from "./attachment-chip.tsx";

const MAX_FILES = 5;
const MAX_BYTES = 25 * 1024 * 1024;
const EXTENSIONS = ["pdf", "jpg", "jpeg", "png", "webp", "gif", "avif", "docx", "txt", "csv"];
const ACCEPT = [
  ".pdf",
  ".jpg",
  ".jpeg",
  ".png",
  ".webp",
  ".gif",
  ".avif",
  ".docx",
  ".txt",
  ".csv",
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/avif",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
  "text/csv",
].join(",");

type PendingFile = {
  key: string;
  name: string;
  kind: Attachment["kind"];
  progress: number;
  status: "uploading" | "done" | "error";
  attachment: Attachment | null;
  abort: (() => void) | null;
};

function kindOf(file: File): Attachment["kind"] {
  if (file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")) return "pdf";
  if (file.type.startsWith("image/")) return "image";
  return "file";
}

function allowed(file: File): boolean {
  const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
  return EXTENSIONS.includes(extension);
}

/** Why the box can't send. `noAccess` shows no hint of its own: the page puts the access notice above the box. */
export type ComposerBlock = "running" | "queued" | "awaitingChoice" | "readOnly" | "noAccess" | null;

/**
 * The message box: auto-growing textarea (Enter sends, Shift+Enter breaks the line),
 * file uploads with progress, and the busy state with Stop.
 */
export function Composer({
  value,
  onChange,
  ensureSession,
  onSend,
  block,
  onStop,
  stopping,
  placeholder,
  autoFocus,
  focusKey,
}: {
  value: string;
  onChange: (value: string) => void;
  /** Returns the session to upload into, creating it first on `/app`. */
  ensureSession: () => Promise<string>;
  onSend: (text: string, attachmentIds: string[]) => Promise<void>;
  block: ComposerBlock;
  onStop?: () => void;
  stopping?: boolean;
  placeholder: string;
  autoFocus?: boolean;
  /** Changing this moves focus into the textarea (for example after an example chip). */
  focusKey?: number;
}) {
  const { t } = useI18n();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<PendingFile[]>([]);
  const [sending, setSending] = useState(false);
  const [dragging, setDragging] = useState(false);
  const disabled = block !== null;

  // Grow with the text, up to a third of the viewport.
  useLayoutEffect(() => {
    const node = textareaRef.current;
    if (!node) return;
    node.style.height = "auto";
    const max = Math.max(160, Math.round(window.innerHeight * 0.33));
    node.style.height = `${Math.min(node.scrollHeight, max)}px`;
    node.style.overflowY = node.scrollHeight > max ? "auto" : "hidden";
  }, [value]);

  useEffect(() => {
    if (focusKey === undefined || focusKey === 0) return;
    const node = textareaRef.current;
    if (!node) return;
    node.focus();
    node.setSelectionRange(node.value.length, node.value.length);
  }, [focusKey]);

  // Abort uploads that are still running when the composer goes away.
  const filesRef = useRef(files);
  useEffect(() => {
    filesRef.current = files;
  }, [files]);
  useEffect(() => () => filesRef.current.forEach((file) => file.status === "uploading" && file.abort?.()), []);

  const update = (key: string, patch: Partial<PendingFile>) =>
    setFiles((current) => current.map((file) => (file.key === key ? { ...file, ...patch } : file)));

  const addFiles = async (list: FileList | File[]) => {
    const incoming = Array.from(list);
    if (incoming.length === 0) return;
    const room = MAX_FILES - files.length;
    if (incoming.length > room) toast.error(t.composer.tooMany);
    const accepted: File[] = [];
    for (const file of incoming.slice(0, Math.max(0, room))) {
      if (!allowed(file)) toast.error(t.composer.badType(file.name));
      else if (file.size > MAX_BYTES) toast.error(t.composer.tooLarge(file.name));
      else accepted.push(file);
    }
    if (accepted.length === 0) return;

    const entries: PendingFile[] = accepted.map((file, index) => ({
      key: `${Date.now()}-${index}-${file.name}`,
      name: file.name,
      kind: kindOf(file),
      progress: 0,
      status: "uploading",
      attachment: null,
      abort: null,
    }));
    setFiles((current) => [...current, ...entries]);

    let sessionId: string;
    try {
      sessionId = await ensureSession();
    } catch (error) {
      toast.error(errorMessage(error, t));
      setFiles((current) => current.filter((file) => !entries.some((entry) => entry.key === file.key)));
      return;
    }

    accepted.forEach((file, index) => {
      const entry = entries[index];
      if (!entry) return;
      const handle = uploadFile(sessionId, file, (fraction) => update(entry.key, { progress: fraction }));
      update(entry.key, { abort: handle.abort });
      handle.promise.then(
        (attachment) => update(entry.key, { status: "done", progress: 1, attachment, abort: null }),
        (error: unknown) => {
          if (error instanceof DOMException && error.name === "AbortError") return;
          update(entry.key, { status: "error", abort: null });
          toast.error(t.composer.uploadFailed(file.name), { description: errorMessage(error, t) });
        },
      );
    });
  };

  const removeFile = (key: string) => {
    const file = files.find((candidate) => candidate.key === key);
    file?.abort?.();
    setFiles((current) => current.filter((candidate) => candidate.key !== key));
  };

  const uploading = files.some((file) => file.status === "uploading");
  const ready = files.filter((file) => file.status === "done" && file.attachment);
  const canSend = !disabled && !sending && !uploading && (value.trim().length > 0 || ready.length > 0);

  const send = async () => {
    if (uploading) {
      toast.message(t.composer.waitUploads);
      return;
    }
    if (!canSend) return;
    setSending(true);
    try {
      await onSend(
        value.trim(),
        ready.map((file) => file.attachment?.id ?? "").filter(Boolean),
      );
      setFiles([]);
    } catch (error) {
      toast.error(t.composer.sendFailed, { description: errorMessage(error, t) });
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      void send();
    }
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    if (!disabled && event.dataTransfer.files.length > 0) void addFiles(event.dataTransfer.files);
  };

  const hint =
    block === "running"
      ? t.composer.busyHint
      : block === "queued"
        ? t.composer.queuedHint
        : block === "awaitingChoice"
          ? t.composer.awaitingChoice
          : block === "readOnly"
            ? t.composer.readOnlyHint
            : null;

  return (
    <div className="flex flex-col gap-2">
      {hint ? (
        <div className="flex items-center justify-between gap-3 rounded-xl border bg-card/95 px-3.5 py-2 text-[12.5px] text-muted-foreground shadow-xs backdrop-blur">
          <span className="flex min-w-0 items-center gap-2">
            {block === "running" || block === "queued" ? (
              <LoaderCircleIcon className="size-3.5 shrink-0 animate-spin text-primary" aria-hidden />
            ) : (
              <CircleAlertIcon className="size-3.5 shrink-0" aria-hidden />
            )}
            <span className="truncate">{hint}</span>
          </span>
          {(block === "running" || block === "queued") && onStop ? (
            <Button variant="outline" size="xs" onClick={onStop} disabled={stopping}>
              <SquareIcon className="size-2.5 fill-current" aria-hidden />
              {stopping ? t.turn.stopping : t.turn.stop}
            </Button>
          ) : null}
        </div>
      ) : null}

      <div
        onDragOver={(event) => {
          if (disabled) return;
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={cn(
          "rounded-2xl border bg-card shadow-[0_4px_18px_-6px_rgba(15,27,45,0.12)] transition-[border-color,box-shadow] focus-within:border-ring/60 focus-within:shadow-[0_0_0_3px_color-mix(in_oklab,var(--ring)_14%,transparent)]",
          dragging && "border-primary border-dashed",
          disabled && "bg-muted/50 shadow-none",
        )}
      >
        {files.length > 0 ? (
          <ul className="flex flex-wrap gap-2 px-3 pt-3">
            {files.map((file) => {
              const Icon = ATTACHMENT_ICONS[file.kind];
              return (
                <li
                  key={file.key}
                  className={cn(
                    "relative flex h-9 max-w-[15rem] min-w-0 items-center gap-2 overflow-hidden rounded-lg border bg-background pr-1 pl-2.5 text-[12.5px]",
                    file.status === "error" && "border-danger-border bg-danger-soft text-danger",
                  )}
                >
                  <Icon className={cn("size-4 shrink-0", file.kind === "pdf" ? "text-danger" : "text-primary")} aria-hidden />
                  <span className="min-w-0 truncate font-medium">{file.name}</span>
                  {file.status === "uploading" ? (
                    <span className="code-mono shrink-0 text-[11px] text-muted-foreground" aria-label={t.composer.uploading}>
                      {Math.round(file.progress * 100)}%
                    </span>
                  ) : null}
                  <Button
                    variant="ghost-muted"
                    size="icon-xs"
                    className="size-6"
                    aria-label={t.composer.removeFile(file.name)}
                    onClick={() => removeFile(file.key)}
                  >
                    <XIcon className="size-3.5" />
                  </Button>
                  {file.status === "uploading" ? (
                    <span
                      className="absolute bottom-0 left-0 h-0.5 bg-primary transition-[width] duration-200"
                      style={{ width: `${Math.max(4, file.progress * 100)}%` }}
                      aria-hidden
                    />
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : null}

        <label className="sr-only" htmlFor="composer-input">
          {t.composer.label}
        </label>
        <textarea
          id="composer-input"
          ref={textareaRef}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={onKeyDown}
          rows={1}
          disabled={disabled}
          autoFocus={autoFocus}
          placeholder={placeholder}
          className="block max-h-[33vh] min-h-[52px] w-full resize-none bg-transparent px-4 pt-3.5 pb-1 text-[14.5px] leading-relaxed text-foreground outline-none placeholder:text-muted-foreground/75 disabled:cursor-not-allowed"
        />

        <div className="flex items-center justify-between gap-2 px-2 pt-1 pb-2">
          <div className="flex min-w-0 items-center gap-1.5">
            <input
              ref={inputRef}
              type="file"
              multiple
              accept={ACCEPT}
              className="hidden"
              tabIndex={-1}
              onChange={(event) => {
                if (event.target.files) void addFiles(event.target.files);
                event.target.value = "";
              }}
            />
            <Tooltip content={t.composer.attachHint}>
              <Button
                variant="ghost-muted"
                size="icon-sm"
                aria-label={t.composer.attach}
                disabled={disabled || files.length >= MAX_FILES}
                onClick={() => inputRef.current?.click()}
              >
                <PaperclipIcon />
              </Button>
            </Tooltip>
            <span className="hidden truncate text-[11.5px] text-muted-foreground lg:inline">{t.composer.attachHint}</span>
          </div>
          <div className="flex shrink-0 items-center gap-2.5">
            <span className="hidden text-[11.5px] text-muted-foreground md:inline">{t.composer.shortcut}</span>
            <Tooltip content={t.composer.send}>
              <Button size="icon-sm" className="rounded-lg" aria-label={t.composer.send} disabled={!canSend} onClick={() => void send()}>
                {sending ? <LoaderCircleIcon className="animate-spin" /> : <ArrowUpIcon />}
              </Button>
            </Tooltip>
          </div>
        </div>
      </div>
    </div>
  );
}
